import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { CALENDAR_DOW, MONTHS_VN } from "../XoSoCompound/constants";

export default function DateSelect({ value, onChange, maxDate }) {
  const selected = dayjs(value);
  const max = maxDate ? dayjs(maxDate) : dayjs();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(selected.startOf("month"));

  useEffect(() => {
    const next = dayjs(value);
    if (next.isValid()) setCursor(next.startOf("month"));
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const cells = useMemo(() => {
    const start = cursor.startOf("month");
    const startWeekday = (start.day() + 6) % 7;
    const items = [];
    for (let i = 0; i < startWeekday; i += 1) {
      items.push({ date: start.subtract(startWeekday - i, "day"), outside: true });
    }
    for (let day = 1; day <= cursor.daysInMonth(); day += 1) {
      items.push({ date: start.date(day), outside: false });
    }
    while (items.length % 7 !== 0) {
      items.push({ date: items[items.length - 1].date.add(1, "day"), outside: true });
    }
    return items;
  }, [cursor]);

  const canPrevDay = true;
  const canNextDay = selected.isBefore(max, "day");
  const canNextMonth = cursor.isBefore(max, "month");
  const dow = CALENDAR_DOW[(selected.day() + 6) % 7];

  const pick = (key) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div className="xoso-sidebar-date" ref={rootRef}>
      <div className="xoso-sidebar-date__row">
        <button
          type="button"
          className="xoso-sidebar-date__nav"
          aria-label="Ngày trước"
          disabled={!canPrevDay}
          onClick={() => onChange(selected.subtract(1, "day").format("YYYY-MM-DD"))}
        >
          <ChevronLeftIcon width={12} height={12} />
        </button>
        <button
          type="button"
          className={`xoso-sidebar-date__value${open ? " is-open" : ""}`}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {dow} {selected.format("DD/MM/YYYY")}
        </button>
        <button
          type="button"
          className="xoso-sidebar-date__nav"
          aria-label="Ngày sau"
          disabled={!canNextDay}
          onClick={() => canNextDay && onChange(selected.add(1, "day").format("YYYY-MM-DD"))}
        >
          <ChevronRightIcon width={12} height={12} />
        </button>
      </div>

      {open ? (
        <div className="xoso-sidebar-cal">
          <div className="xoso-sidebar-cal__nav">
            <button
              type="button"
              className="xoso-sidebar-date__nav"
              aria-label="Tháng trước"
              onClick={() => setCursor((c) => c.subtract(1, "month"))}
            >
              <ChevronLeftIcon width={12} height={12} />
            </button>
            <div className="xoso-sidebar-cal__month">
              {MONTHS_VN[cursor.month()]} {cursor.year()}
            </div>
            <button
              type="button"
              className="xoso-sidebar-date__nav"
              aria-label="Tháng sau"
              disabled={!canNextMonth}
              onClick={() => canNextMonth && setCursor((c) => c.add(1, "month"))}
            >
              <ChevronRightIcon width={12} height={12} />
            </button>
          </div>
          <div className="xoso-sidebar-cal__dow">
            {CALENDAR_DOW.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="xoso-sidebar-cal__grid">
            {cells.map(({ date, outside }) => {
              const key = date.format("YYYY-MM-DD");
              const disabled = date.isAfter(max, "day");
              const isSelected = date.isSame(selected, "day");
              const isToday = date.isSame(dayjs(), "day");
              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  className={[
                    "xoso-sidebar-cal__day",
                    outside ? "is-outside" : "",
                    isSelected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                  ].join(" ")}
                  onClick={() => pick(key)}
                >
                  {date.date()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
